import React, { createContext, useContext, useState, useEffect } from 'react';
import { ref as dbRef, get, onValue } from 'firebase/database';
import { database } from '../config/firebase';

/**
 * CompanyContext — fornisce companyId, role (owner/staff) e stato abbonamento
 * all'intera app admin.
 *
 * Il lookup di companyId/role avviene in due passaggi, coerente con lo schema
 * Rules della Fase 1:
 *   1. adminIndex/{uid}          -> companyId
 *   2. companies/{companyId}/admins/{uid} -> role ('owner' | 'staff')
 *
 * FASE 8: aggiunto un listener in tempo reale su companies/{companyId}/info
 * per i campi del ciclo di vita abbonamento (subscriptionStatus, trialEndsAt,
 * gracePeriodEndsAt, hardDeleteAt) — necessari al banner countdown e al
 * blocco delle funzionalità quando l'abbonamento non è in regola. È un
 * listener live (onValue), non una singola lettura, perché lo stato può
 * cambiare mentre l'utente ha il pannello aperto (webhook Stripe, o il
 * controllo giornaliero schedulato).
 *
 * NOTA IMPORTANTE: questo hook determina cosa MOSTRARE in UI, non è una misura
 * di sicurezza. L'enforcement reale è nelle Firebase Rules (Fase 1) e nelle
 * Cloud Functions (Fase 7/8) — un utente potrebbe teoricamente alterare questo
 * stato lato client, ma le Rules rifiuterebbero comunque scritture non autorizzate.
 */

const DEFAULT_STATE = {
  companyId: null,
  role: null,
  isAdmin: false,
  isOwner: false,
  isPlatformAdmin: false,
  loading: true,
  // Stato abbonamento (Fase 8)
  subscriptionStatus: null,            // 'trial' | 'active' | 'grace_period' | 'expired' | 'cancelled'
  subscriptionPlan: null,              // 'trial' | 'base' | 'pro' | 'business'
  subscriptionBillingInterval: 'month', // 'month' | 'year' — Bug 2 FIX
  trialEndsAt: null,
  gracePeriodEndsAt: null,
  hardDeleteAt: null,
  // Sospensione manuale da parte del platform admin — indipendente dal ciclo
  // di vita abbonamento sopra (vedi setCompanyBlocked in functions/index.js).
  accountBlocked: false,
  // Addon e contatori
  vehicleCount: null,
  storageAddonActive: false,
  infoPiva: null,
  infoRagioneSociale: null,
  infoTelefono: null,
  infoEmail: null,
  refresh: () => {},
};

const CompanyContext = createContext(DEFAULT_STATE);

export function useCompany() {
  return useContext(CompanyContext);
}

export function CompanyProvider({ user, children }) {
  const [state, setState] = useState(DEFAULT_STATE);

  // Contatore usato per forzare un nuovo giro di resolveCompany() anche
  // quando l'oggetto `user` non cambia di riferimento (Firebase Auth muta
  // l'utente in-place, quindi setUser(stessoUser) non fa ripartire l'effetto
  // sotto). Va incrementato subito dopo un'operazione che modifica
  // adminIndex/companies su RTDB (es. subito dopo registerCompany), tramite
  // la funzione `refresh` esposta nel context.
  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = () => setRefreshTick(t => t + 1);

  // ── Passo 1+2: risolve companyId e role (rieseguito al login/logout,
  //     oppure manualmente tramite refresh()) ──
  useEffect(() => {
    let cancelled = false;

    async function resolveCompany() {
      if (!user) {
        if (!cancelled) setState({ ...DEFAULT_STATE, loading: false });
        return;
      }

      try {
        const platformAdminSnap = await get(dbRef(database, `platformAdmins/${user.uid}`));
        const isPlatformAdmin = platformAdminSnap.exists();

        const indexSnap = await get(dbRef(database, `adminIndex/${user.uid}`));
        const companyId = indexSnap.exists() ? indexSnap.val() : null;

        let role = null;
        if (companyId) {
          const adminSnap = await get(
            dbRef(database, `companies/${companyId}/admins/${user.uid}/role`)
          );
          role = adminSnap.exists() ? adminSnap.val() : null;
        }

        if (!cancelled) {
          setState(s => ({
            ...s,
            companyId,
            role,
            isAdmin: Boolean(companyId && role) || isPlatformAdmin,
            isOwner: role === 'owner',
            isPlatformAdmin,
            loading: false,
          }));
        }
      } catch (error) {
        console.error('Errore risoluzione company/ruolo admin:', error);
        if (!cancelled) setState({ ...DEFAULT_STATE, loading: false });
      }
    }

    resolveCompany();
    return () => { cancelled = true; };
  }, [user, refreshTick]);

  // ── Listener live sullo stato abbonamento, una volta noto companyId ──
  useEffect(() => {
    if (!state.companyId) return;

    const infoRef = dbRef(database, `companies/${state.companyId}/info`);
    const unsubscribe = onValue(infoRef, (snapshot) => {
      const info = snapshot.val() || {};
      setState(s => ({
        ...s,
        subscriptionStatus: info.subscriptionStatus || null,
        subscriptionPlan: info.subscriptionPlan || null,
        subscriptionBillingInterval: info.subscriptionBillingInterval || 'month', // Bug 2 FIX
        trialEndsAt: info.trialEndsAt || null,
        gracePeriodEndsAt: info.gracePeriodEndsAt || null,
        hardDeleteAt: info.hardDeleteAt || null,
        accountBlocked: info.accountBlocked === true,
        vehicleCount: info.vehicleCount ?? null,
        storageAddonActive: info.storageAddonActive === true,
        infoPiva: info.piva || null,
        infoRagioneSociale: info.ragioneSociale || null,
        infoTelefono: info.telefono || null,
        infoEmail: info.email || null,
      }));
    });

    return () => unsubscribe();
  }, [state.companyId]);

  return (
    <CompanyContext.Provider value={{ ...state, refresh }}>
      {children}
    </CompanyContext.Provider>
  );
}
